import React, { useState } from 'react';
import {
    Bold, Italic, Underline, List, ListOrdered, Heading2, Link, Code, Table,
    AlignLeft, AlignCenter, AlignRight, Copy, Download, Eye, EyeOff,
    Trash2, FileDown
} from 'lucide-react';
import './RichTextEditor.css';

export default function RichTextEditor({ initialContent = '', onChange }) {
    const [content, setContent] = useState(initialContent);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [fontSize, setFontSize] = useState('16px');
    const [lineHeight, setLineHeight] = useState('1.6');
    const [showTableModal, setShowTableModal] = useState(false);
    const [tableRows, setTableRows] = useState(3);
    const [tableCols, setTableCols] = useState(3);
    const [showSourceCode, setShowSourceCode] = useState(false);
    const [documentName, setDocumentName] = useState('Untitled Document');
    const [history, setHistory] = useState(['']);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const editorRef = React.useRef(null);

    const execCommand = (cmd, value = null) => {
        document.execCommand(cmd, false, value);
        editorRef.current?.focus();
        updateButtonStates();
    };

    const updateButtonStates = () => {
        setIsBold(document.queryCommandState('bold'));
        setIsItalic(document.queryCommandState('italic'));
        setIsUnderline(document.queryCommandState('underline'));
    };

    const updateHistory = (html) => {
        setHistory(prevHistory => {
            const newHistory = prevHistory.slice(0, historyIndex + 1);
            newHistory.push(html);
            return newHistory;
        });
        setHistoryIndex(prevIndex => prevIndex + 1);
    };

    const undo = () => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setHistoryIndex(newIndex);
            setTimeout(() => {
                editorRef.current.innerHTML = history[newIndex];
                const event = new Event('input', { bubbles: true });
                editorRef.current.dispatchEvent(event);
            }, 0);
        }
    };

    const redo = () => {
        if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1;
            setHistoryIndex(newIndex);
            setTimeout(() => {
                editorRef.current.innerHTML = history[newIndex];
                const event = new Event('input', { bubbles: true });
                editorRef.current.dispatchEvent(event);
            }, 0);
        }
    };

    const createTable = () => {
        const rows = parseInt(tableRows);
        const cols = parseInt(tableCols);

        if (rows > 0 && cols > 0) {
            let table = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;"><tbody>';
            for (let i = 0; i < rows; i++) {
                table += '<tr>';
                for (let j = 0; j < cols; j++) {
                    table += `<td style="border: 2px solid #333; padding: 12px; text-align: left; min-width: 80px;">Cell</td>`;
                }
                table += '</tr>';
            }
            table += '</tbody></table><br/>';

            editorRef.current.focus();
            document.execCommand('insertHTML', false, table);
            setShowTableModal(false);
            handleContentChange({ currentTarget: editorRef.current });
        }
    };

    const insertImage = () => {
        const url = prompt('Enter image URL:');
        if (url) {
            execCommand('insertImage', url);
            handleContentChange({ currentTarget: editorRef.current });
        }
    };

    const insertLink = () => {
        const url = prompt('Enter URL:', 'https://');
        if (url) {
            execCommand('createLink', url);
            handleContentChange({ currentTarget: editorRef.current });
        }
    };

    const insertYouTubeVideo = () => {
        const videoId = prompt('Enter YouTube Video ID:', '');
        if (videoId) {
            const embed = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="margin: 10px 0;"></iframe><br/>`;
            editorRef.current.focus();
            document.execCommand('insertHTML', false, embed);
            handleContentChange({ currentTarget: editorRef.current });
        }
    };

    const handleContentChange = (e) => {
        const html = e.currentTarget.innerHTML;
        setContent(html);
        updateHistory(html);

        if (onChange) onChange(html);

        const text = e.currentTarget.innerText;
        const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
        setWordCount(words);
        setCharCount(text.length);
        updateButtonStates();
    };

    const handleClear = () => {
        if (window.confirm('Are you sure you want to clear all content?')) {
            editorRef.current.innerHTML = '';
            setContent('');
            setWordCount(0);
            setCharCount(0);
            updateHistory('');
        }
    };

    const downloadAsHTML = () => {
        const htmlContent = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>${documentName}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; line-height: ${lineHeight}; font-size: ${fontSize}; }
                    table { border-collapse: collapse; width: 100%; }
                    td, th { border: 1px solid #333; padding: 10px; }
                    img { max-width: 100%; height: auto; }
                </style>
            </head>
            <body>
                <h1>${documentName}</h1>
                <hr>
                ${editorRef.current.innerHTML}
                <hr>
                <p><small>Created on: ${new Date().toLocaleString()}</small></p>
            </body>
            </html>`;
        const element = document.createElement('a');
        const file = new Blob([htmlContent], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        element.download = `${documentName}.html`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const downloadAsPlainText = () => {
        const text = editorRef.current.innerText;
        const element = document.createElement('a');
        const file = new Blob([text], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${documentName}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(editorRef.current.innerHTML);
        alert('HTML content copied to clipboard!');
    };

    const printDocument = () => {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write(`<html><head><title>${documentName}</title></head><body>${editorRef.current.innerHTML}</body></html>`);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="rte-container">
            <div className="rte-max-width">
                <div className="rte-header">
                    <div>
                        <h1 className="rte-title">Content</h1>
                        <p className="rte-subtitle">Enter the main content for the blog below</p>
                    </div>
                </div>

                <div className="rte-main-container">
                    {/* Toolbar 1 */}
                    <div className="rte-toolbar-section">
                        <span className="rte-toolbar-label">TEXT</span>

                        <button
                            type="button"
                            onClick={() => execCommand('bold')}
                            className={`rte-button ${isBold ? 'active' : ''}`}
                            title="Bold"
                        >
                            <Bold size={18} /> B
                        </button>

                        <button
                            type="button"
                            onClick={() => execCommand('italic')}
                            className={`rte-button ${isItalic ? 'active' : ''}`}
                            title="Italic"
                        >
                            <Italic size={18} /> I
                        </button>

                        <button
                            type="button"
                            onClick={() => execCommand('underline')}
                            className={`rte-button ${isUnderline ? 'active' : ''}`}
                            title="Underline"
                        >
                            <Underline size={18} /> U
                        </button>

                        <div className="rte-divider" />

                        <select onChange={(e) => setFontSize(e.target.value)} value={fontSize} className="rte-select">
                            <option value="12px">12px</option>
                            <option value="14px">14px</option>
                            <option value="16px">16px</option>
                            <option value="20px">20px</option>
                            <option value="24px">24px</option>
                        </select>

                        <select onChange={(e) => setLineHeight(e.target.value)} value={lineHeight} className="rte-select">
                            <option value="1">1.0</option>
                            <option value="1.6">1.6</option>
                            <option value="2">2.0</option>
                        </select>
                    </div>

                    {/* Toolbar 2 */}
                    <div className="rte-toolbar-section">
                        <span className="rte-toolbar-label">FORMAT</span>

                        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="rte-button">
                            <List size={18} />
                        </button>

                        <button type="button" onClick={() => execCommand('insertOrderedList')} className="rte-button">
                            <ListOrdered size={18} />
                        </button>

                        <div className="rte-divider" />

                        <button type="button" onClick={() => execCommand('formatBlock', '<h1>')} className="rte-button">H1</button>
                        <button type="button" onClick={() => execCommand('formatBlock', '<h2>')} className="rte-button">H2</button>
                        <button type="button" onClick={() => execCommand('formatBlock', '<p>')} className="rte-button">P</button>

                        <div className="rte-divider" />

                        <button type="button" onClick={() => execCommand('justifyLeft')} className="rte-button"><AlignLeft size={18} /></button>
                        <button type="button" onClick={() => execCommand('justifyCenter')} className="rte-button"><AlignCenter size={18} /></button>
                        <button type="button" onClick={() => execCommand('justifyRight')} className="rte-button"><AlignRight size={18} /></button>
                    </div>

                    {/* Toolbar 3 */}
                    <div className="rte-toolbar-section">
                        <span className="rte-toolbar-label">INSERT</span>

                        <button type="button" onClick={insertLink} className="rte-button"><Link size={18} /> Link</button>
                        <button type="button" onClick={insertImage} className="rte-button">🖼️ Image</button>
                        <button type="button" onClick={() => setShowTableModal(true)} className="rte-button"><Table size={18} /> Table</button>
                        <button type="button" onClick={insertYouTubeVideo} className="rte-button">▶️ Video</button>
                        <button type="button" onClick={() => execCommand('insertHorizontalRule')} className="rte-button">─ Line</button>
                    </div>

                    {/* Toolbar 4 */}
                    <div className="rte-toolbar-section">
                        <span className="rte-toolbar-label">TOOLS</span>

                        <button type="button" onClick={undo} disabled={historyIndex <= 0} className="rte-button">↶ Undo</button>
                        <button type="button" onClick={redo} disabled={historyIndex >= history.length - 1} className="rte-button">↷ Redo</button>

                        <div className="rte-divider" />

                        <button type="button" onClick={copyToClipboard} className="rte-button"><Copy size={18} /></button>
                        <button type="button" onClick={() => setShowSourceCode(!showSourceCode)} className="rte-button">{showSourceCode ? <Eye size={18} /> : <EyeOff size={18} />}</button>
                        <button type="button" onClick={printDocument} className="rte-button">🖨️ Print</button>
                        <button type="button" onClick={downloadAsHTML} className="rte-button"><FileDown size={18} /> HTML</button>
                        <button type="button" onClick={downloadAsPlainText} className="rte-button">📄 TXT</button>
                        <button type="button" onClick={handleClear} className="rte-button rte-button-danger">
                            <Trash2 size={18} /> Clear
                        </button>
                    </div>

                    {/* Editor & Preview */}
                    <div className="rte-editor-container">
                        <div className="rte-editor-col">
                            <label className="rte-col-label">✏️ EDITOR</label>
                            {/*<div*/}
                            {/*    ref={editorRef}*/}
                            {/*    contentEditable*/}
                            {/*    className="rte-editor"*/}
                            {/*    style={{ fontSize: fontSize, lineHeight: lineHeight }}*/}
                            {/*    onInput={handleContentChange}*/}
                            {/*    dangerouslySetInnerHTML={{ __html: content }}*/}
                            {/*/>*/}

                            <div
                                ref={editorRef}
                                className="rte-editor"
                                contentEditable
                                    style={{ fontSize: fontSize, lineHeight: lineHeight }}
                                onInput={handleContentChange}
                            >
                            </div>

                        </div>

                        <div className="rte-editor-col">
                            <label className="rte-col-label">{showSourceCode ? '{ } SOURCE CODE' : '👁️ PREVIEW'}</label>
                            {showSourceCode ? (
                                <textarea
                                    value={content}
                                    readOnly
                                    className="rte-source-code"
                                />
                            ) : (
                                <div
                                    className="rte-preview"
                                    dangerouslySetInnerHTML={{ __html: content }}
                                />
                            )}
                        </div>
                    </div>

                    {/* Status Bar */}
                    <div className="rte-status-bar">
                        <div className="rte-stat-item">
                            <span>Words:</span>
                            <span className="rte-stat-value">{wordCount}</span>
                        </div>
                        <div className="rte-stat-item">
                            <span>Characters:</span>
                            <span className="rte-stat-value">{charCount}</span>
                        </div>
                        <div className="rte-stat-item">
                            <span>⏰ {new Date().toLocaleTimeString()}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Modal */}
            {showTableModal && (
                <div className="rte-modal">
                    <div className="rte-modal-content">
                        <h2 className="rte-modal-title">Insert Table</h2>

                        <label className="rte-input-label">Rows:</label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={tableRows}
                            onChange={(e) => setTableRows(e.target.value)}
                            className="rte-input-field"
                        />

                        <label className="rte-input-label">Columns:</label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={tableCols}
                            onChange={(e) => setTableCols(e.target.value)}
                            className="rte-input-field"
                        />

                        <div className="rte-preview-table">
                            <p className="rte-preview-table-title">Preview:</p>
                            <div className="rte-preview-table-container">
                                <table className="rte-table">
                                    <tbody>
                                    {Array(Math.min(parseInt(tableRows) || 1, 5)).fill(0).map((_, i) => (
                                        <tr key={i}>
                                            {Array(Math.min(parseInt(tableCols) || 1, 5)).fill(0).map((_, j) => (
                                                <td key={j} className="rte-table-cell">({i+1},{j+1})</td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="rte-modal-buttons">
                            <button type="button" onClick={createTable} className="rte-modal-btn-primary">Insert Table</button>
                            <button type="button" onClick={() => setShowTableModal(false)} className="rte-modal-btn-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}